import React, {FC, useMemo} from 'react';
import {View, StyleSheet, Platform} from 'react-native';
import {DeviceSize} from '../../../shared/theme/helpers';
import {Button} from '../../../shared/components/button';
import {BaseField, InputMaskTypes} from '../../../shared/components/baseField';
import {LoginState} from '../../../shared/models/login/loginStep';
import {PasswordFields} from './passwordFields';
import {SmsFields} from './smsFields';
import {useStyles, StyleHelper, NamedStyles} from '../../../shared/hooks/styles';
import {Field} from 'redux-form';
import {Agreement} from '../components/agreement';
import {IconProps} from '../../../shared/components/svg/model';
import {defaults} from '../../../shared/constants/defaults';
import {LoginType, SendCodeChannelType} from '../../../shared/models/api';
import {Form} from '../../../shared/components/form';
import {EmailFields} from './emailFields';
import {QrCodeIcon} from '../components/qrCodeIcon';
import {formatIdentificator, formatLoginLabel} from '../formatters';
import {useIsDesktop} from '../../../shared/hooks/useIsDesktop';

const formWidthDesktop = 374;

export interface Props {
  step: number;
  mask: InputMaskTypes;
  disabled: boolean;
  onSubmit: (e?: any) => void;
  onRefresh: (channelType?: SendCodeChannelType) => Promise<boolean>;
  onRecoveryByEmail: () => void;
  loginType?: LoginType;
  onAutoFill: (data: any) => void;
  submitting: boolean;
}

export default (props: Props) => {
  const {step, mask, onSubmit, disabled, onRefresh, onRecoveryByEmail, loginType, onAutoFill, submitting} = props;
  const styles = useStyles((styleHelper: StyleHelper) => createStyles(styleHelper));
  const isDesktop = useIsDesktop();

  const buttonProps: {
    text: string;
    icon?: FC<IconProps>;
    iconProps?: IconProps;
  } = useMemo(() => {
    if (step === LoginState.registrationPasswords) return {text: 'Войти'};
    if (step === LoginState.forgotPassword || step === LoginState.forgotPasswordNotCard)
      return {text: 'Получить SMS-код'};
    if (step === LoginState.forgotPasswordPasswords) return {text: 'Сохранить и войти'};
    if (step === LoginState.agreement) return {text: 'Получить SMS-код'};
    return {text: 'Далее'};
  }, [step]);

  const secondButtonProps: {
    text: string;
    onPress?: () => void;
  } | null = useMemo(() => {
    if (step === LoginState.forgotPassword || step === LoginState.forgotPasswordNotCard) {
      return {
        text: 'Получить эл. письмо',
        onPress: onRecoveryByEmail,
      };
    }
    return null;
  }, [step]);

  return (
    <Form style={styles.container} onSubmit={onSubmit}>
      {step === LoginState.forgotPasswordPasswords ||
      step === LoginState.registrationPasswords ||
      step === LoginState.agreement ? null : (
        <View style={[styles.fieldContainer, styles.fieldContainerWithMargin]}>
          <Field
            component={BaseField}
            mask={mask}
            label={formatLoginLabel(loginType)}
            type={loginType === LoginType.PhoneNumber ? 'phone' : 'text'}
            name='identificator'
            format={(v: any) => formatIdentificator(v, loginType)}
            style={isDesktop ? {width: formWidthDesktop} : undefined}
            disabled
          />
        </View>
      )}
      {step === LoginState.agreement ? <Agreement /> : null}
      {step === LoginState.forgotPasswordNotCard ? (
        <View style={[styles.fieldContainer]}>
          <Field
            component={BaseField}
            label='Номер карты'
            type='digits'
            name='cardNumber'
            maxLength={defaults.maxLoginLength}
            onSubmitEditing={onSubmit}
            style={isDesktop ? {width: formWidthDesktop} : undefined}
            props={{
              right: Platform.OS !== 'web' && <QrCodeIcon onScan={onAutoFill} adjustFor='input' />,
            }}
          />
        </View>
      ) : null}
      {step === LoginState.forgotPasswordCode || step === LoginState.registrationCode ? (
        <SmsFields onRefresh={onRefresh} onSubmit={onSubmit} />
      ) : null}
      {step === LoginState.forgotPasswordEmail && (
        <EmailFields onRefresh={() => onRefresh(SendCodeChannelType.Email)} />
      )}
      {step === LoginState.forgotPasswordPasswords || step === LoginState.registrationPasswords ? (
        <PasswordFields onSubmit={onSubmit} />
      ) : null}
      {step !== LoginState.forgotPasswordEmail && (
        <View style={styles.buttonContainer}>
          <Button
            id='registration-primary'
            disabled={disabled || submitting}
            type={secondButtonProps ? 'primary' : 'transparent'}
            style={styles.button}
            onPress={onSubmit}
            {...buttonProps}
          />
          {secondButtonProps ? (
            <Button
              id='registration-secondary'
              type='secondary'
              style={styles.button}
              {...secondButtonProps}
              disabled={submitting}
            />
          ) : null}
        </View>
      )}
    </Form>
  );
};

const fl = StyleSheet.flatten;

export const createStyles = (styleHelper: StyleHelper): NamedStyles<any> => {
  const {mediaOnly, align, theme} = styleHelper;

  return {
    container: fl([
      {display: 'flex', justifyContent: 'center', flexGrow: 1, flexShrink: 0, flexDirection: 'column'},
      mediaOnly(
        DeviceSize.desktop,
        {width: formWidthDesktop, marginTop: 14},
        {width: 295, marginTop: 0, paddingTop: 18},
      ),
    ]),
    innerButtonText: {
      color: theme.color.primaryColor,
      marginRight: 14,
    },
    innerButton: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      color: theme.color.primaryColor,
    },
    textInnerContainer: {
      ...theme.fonts.bodySmall,
    },
    button: fl([
      {
        marginBottom: 0,
      },
      mediaOnly(
        DeviceSize.desktop,
        {minWidth: 240, width: 'auto', marginTop: 32},
        {
          marginTop: align(20),
          minWidth: 220,
          width: 'auto',
          paddingHorizontal: 4,
        },
      ),
    ]),
    fieldContainerWithMargin: {
      marginBottom: 20,
    },
    fieldContainer: {
      ...mediaOnly(DeviceSize.desktop, {}, {marginTop: 0}),
    },
    buttonContainer: {
      marginTop: 'auto',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
    },
  };
};
